import { Button } from "../ui/Button"
import { useTranslation } from "react-i18next"
import { Dialog, DialogProps } from "@/components/ui/Dialog"
import { CollectionResponseDTO, PhotoResponseDTO } from "@/api/PSApiOperations"
import { useEffect, useRef, useState, useMemo } from "react"
import { ProgressBar } from "../ui/ProgressBar"
import {
	checkIfStyleCreationIsCurrent,
	confirmCancelStyleCreation,
	createStyleDraftFromCGPhotos,
	createStyleDraftFromFiles,
	getDefaultStyleName,
} from "@/store/style/create-style"
import toast from "react-hot-toast"
import { popDialog } from "@/store/dialogs-state"
import {
	currentStyleCreationIdAtom,
	ProjectStyleTypes,
	showStylePreviewAndNamingDialog,
	StyleDraft,
} from "@/store/style/style-state"
import { useSetAtom } from "jotai"
import { getRandomReferencePhotosFromCollection } from "@/store/Pixieset/store-client-gallery"

import { getRandomPhotosForStylePreview } from "@/store/photo/photos-state"
import { StyleReferenceImage } from "../Styles/StyleReferenceImage"
import { Icon } from "../icons/Icon"
import type { CreateStyleSelectOptions } from "./CreateStyleDialog"

export const StyleCreateLoadingDialog = (
	props: DialogProps & {
		styleCreationId: string
		projectStyleType: ProjectStyleTypes
		onStyleSelected: (option: CreateStyleSelectOptions) => void | Promise<void>
		referenceFiles?: File[]
		collection?: CollectionResponseDTO
	},
) => {
	const { referenceFiles, collection } = props
	const { t } = useTranslation("components")

	const [progress, setProgress] = useState(0)
	const [referencePhotos, setReferencePhotos] = useState<PhotoResponseDTO[] | undefined>(
		undefined,
	)
	const isCancellingRef = useRef(false)
	const [createdStyle, setCreatedStyle] = useState<StyleDraft | null>(null)
	const [fileThumbnails, setFileThumbnails] = useState<Record<number, Blob>>({})

	// Combine files and photos into a single array for the filmstrip.
	// For files: never pass the raw File — only render once a resized thumbnail is available.
	type CellProps = { imageData?: Blob } | { remotePhoto: PhotoResponseDTO }
	const images: CellProps[] = referenceFiles
		? referenceFiles.map((_file, idx) => ({ imageData: fileThumbnails[idx] }))
		: referencePhotos
			? referencePhotos.map((photo) => ({ remotePhoto: photo }))
			: []

	const count = images.length
	const totalProgress = Math.round(count * 2) // download/extract + processing each photo
	const setCurrentStyleCreationId = useSetAtom(currentStyleCreationIdAtom)

	// Marquee animation state
	const IMAGE_SIZE_REM = 7
	const totalImages = images.length

	useEffect(() => {
		if (!isCancellingRef.current && createdStyle) {
			handleStyleDraftCreated(createdStyle)
		}
	}, [isCancellingRef.current, createdStyle])

	const marqueeStyleCalc = useMemo(() => {
		const imageWidth = IMAGE_SIZE_REM * 16
		const gapWidth = 16
		const singleSetWidth = images.length * imageWidth + (images.length - 1) * gapWidth
		const duration = Math.max(10, images.length)

		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return {
			"--marquee-start": "0px",
			"--marquee-end": `-${singleSetWidth}px`,
			"--marquee-duration": `${duration}s`,
		} as React.CSSProperties
	}, [images.length])

	useEffect(() => {
		if (checkIfStyleCreationIsCurrent(props.styleCreationId)) return
		setCurrentStyleCreationId(props.styleCreationId)

		if (referenceFiles) {
			void createStyleDraftFromFiles({
				styleCreationId: props.styleCreationId,
				styleName: getDefaultStyleName(),
				files: referenceFiles,
				onProgress: (delta) => setProgress((prev) => prev + delta),
				onThumbnail: (idx, blob) => setFileThumbnails((prev) => ({ ...prev, [idx]: blob })),
			})
				.then((style) => {
					handleStyleDraftCreated(style)
				})
				.catch((e) => {
					console.error("Failed to create style from files", e)
					popDialog()
					toast.error(t("styleCreateLoadingDialog.failedToCreateStyleFrom"))
				})
				.finally(() => {
					setProgress(0)
				})
		} else if (collection) {
			void getRandomReferencePhotosFromCollection(collection.id, 50)
				.then((photos) => {
					setReferencePhotos(photos)

					void createStyleDraftFromCGPhotos({
						styleCreationId: props.styleCreationId,
						styleName: `${collection.name} Style`,
						photos,
						onProgress: (delta) => setProgress((prev) => prev + delta),
					})
						.then((style) => {
							if (style) {
								handleStyleDraftCreated(style)
							}
						})
						.catch((e) => {
							console.error("Failed to create style from Collection", e)
							popDialog()
							toast.error(t("styleCreateLoadingDialog.failedToCreateStyleFrom2"))
						})
						.finally(() => {
							setProgress(0)
						})
				})
				.catch((e) => {
					console.error("Failed to fetch photos from Collection", e)
					popDialog()
					toast.error(t("styleCreateLoadingDialog.failedToFetchPhotosFrom"))
				})
		}
	}, [])

	const handleStyleDraftCreated = (styleDraft: StyleDraft) => {
		if (!checkIfStyleCreationIsCurrent(props.styleCreationId)) return

		if (isCancellingRef.current) {
			// If user is cancelling, just set the created style but don't show the dialog
			setCreatedStyle(styleDraft)
			return
		}

		const previewPhotos = getRandomPhotosForStylePreview()

		showStylePreviewAndNamingDialog(
			{ ...props, closeConfirm: confirmCancelStyleCreation },
			styleDraft,
			props.projectStyleType,
			previewPhotos,
			(style) => {
				if ("styleId" in style) {
					console.warn(
						"[StyleCreateLoadingDialog] Unexpected local style during creation.",
					)
					return
				}
				return props.onStyleSelected({
					styleDraft: style,
					projectStyleType: props.projectStyleType,
				})
			},
		)
	}

	const handleCancel = async () => {
		isCancellingRef.current = true
		await confirmCancelStyleCreation()
		isCancellingRef.current = false
	}

	const renderCell = (imgProps: CellProps, idx: number) => {
		const hasImage = "imageData" in imgProps ? Boolean(imgProps.imageData) : true
		return (
			<div
				key={idx}
				className="flex items-center justify-center"
				style={{
					width: `${IMAGE_SIZE_REM}rem`,
					minWidth: `${IMAGE_SIZE_REM}rem`,
					maxWidth: `${IMAGE_SIZE_REM}rem`,
				}}
			>
				{hasImage ? (
					<StyleReferenceImage {...imgProps} />
				) : (
					<div className="relative flex aspect-square size-28 items-center justify-center overflow-hidden border-2 border-base-700 bg-base-700">
						<Icon name="photo" className="animate-pulse" />
					</div>
				)}
			</div>
		)
	}

	const renderAnimatedImages = () => {
		return (
			<div className="relative h-32 w-full overflow-hidden">
				{/* Fading overlays */}
				<div className="pointer-events-none absolute left-0 top-0 z-10 -ml-4 h-full w-28 bg-gradient-to-r from-base-800 to-transparent" />
				<div className="pointer-events-none absolute right-0 top-0 z-10 -mr-4 h-full w-28 bg-gradient-to-l from-base-800 to-transparent" />
				{/* Marquee images */}
				<div className="flex animate-marquee gap-x-4" style={marqueeStyleCalc}>
					{[...images, ...images].map((imgProps, idx) => renderCell(imgProps, idx))}
				</div>
			</div>
		)
	}

	const renderStaticImages = () => {
		return (
			<div className="flex gap-x-4">
				{images.map((imgProps, idx) => renderCell(imgProps, idx))}
			</div>
		)
	}

	const renderImages = () => {
		return totalImages > 5 ? renderAnimatedImages() : renderStaticImages()
	}

	return (
		<Dialog
			contentSize="md"
			{...props}
			backConfirm={() => {
				isCancellingRef.current = true
				return confirmCancelStyleCreation().then((confirmed) => {
					isCancellingRef.current = false
					return confirmed
				})
			}}
			bottomButtons={
				<div className="flex w-full flex-1 flex-row items-center justify-end space-x-2">
					<Button
						trackingLabel="cancel_style_creation"
						aria-label={t("styleCreateLoadingDialog.ariaCancelCreation")}
						onClick={() => void handleCancel()}
					>
						{t("common:action.cancel")}
					</Button>
				</div>
			}
			disableClose={true}
		>
			<div className="flex size-full flex-col space-y-4">
				<div className="text-md font-bold">{t("styleCreateLoadingDialog.creating")}</div>
				<div className="flex h-full flex-1 flex-col items-center justify-center space-y-4 overflow-hidden">
					{renderImages()}
					<div className="text-sm text-base-400">
						{t("styleCreateLoadingDialog.analyzing")}
					</div>
					<div className="h-fit w-full">
						<ProgressBar max={totalProgress} value={progress} />
					</div>
				</div>
			</div>
		</Dialog>
	)
}
